// commerce.ts — Shopify-sourced behaviour for the buy rail: live price hydration
// and add-to-the-normal-cart. ALL of this is a no-op unless the product carries an
// active `shopify` binding, so every existing product keeps its made-to-order path
// (computePrice + draft-order checkout) untouched.
//
// Two responsibilities:
//   1. hydrateLivePrice — after the rail paints the instant local price, fetch the
//      real Shopify total async and overlay it (optimistic UI, stale-guarded).
//   2. buyShopify — turn the configuration into real Shopify pieces in the NORMAL
//      cart: a cart permalink when standalone, or a postMessage to the embed snippet
//      (which calls /cart/add.js, carrying the configuration as line properties).

import type { ProductConfig, ConfigState } from "@/types";
import { resolveBOM } from "@/state/bom";
import { fetchCatalog, type CatalogLine, type AvailMap } from "@/state/catalog";
import { goToCheckout } from "@/state/checkout";

/** True when this product should source price + cart from Shopify. */
export function shopifyActive(product: ProductConfig): boolean {
  return product.shopify?.mode === "shopify-sourced";
}

export interface LivePrice {
  total: number;
  currency: string;
  allAvailable: boolean;
  lines: CatalogLine[];
  /** Whole-product stock map, for greying out-of-stock options. */
  availability: AvailMap;
}

let liveSeq = 0;
let debounceTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Fetch the live Shopify total for the current state and hand it to `apply`. The
 * caller has ALREADY painted the local computePrice, so this only overlays the
 * truth when it arrives. `apply(null)` means "degrade — keep the local price".
 * Stale-guarded (only the newest call wins) and lightly debounced so dragging
 * through swatches doesn't hammer the proxy.
 */
export function hydrateLivePrice(
  slug: string | null,
  product: ProductConfig,
  state: ConfigState,
  apply: (price: LivePrice | null) => void,
): void {
  if (!shopifyActive(product) || !slug) return;
  const seq = ++liveSeq;
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    fetchCatalog(slug, state)
      .then((res) => {
        if (seq !== liveSeq) return; // a newer selection superseded this fetch
        if (res.ok) {
          apply({
            total: res.total,
            currency: res.currency,
            allAvailable: res.allAvailable,
            lines: res.lines,
            availability: res.availability ?? {},
          });
        } else {
          apply(null); // not-shopify / unmapped / shopify-error → keep local price
        }
      })
      .catch(() => {
        if (seq === liveSeq) apply(null);
      });
  }, 120);
}

/** Human-readable configuration, attached to each cart line so it shows on the
 *  cart + order (Shopify line item properties). */
function configProperties(product: ProductConfig, state: ConfigState): Record<string, string> {
  const props: Record<string, string> = {};
  const forme = product.formes.find((f) => f.id === state.formeId);
  if (forme?.label) props["Shape"] = forme.label;
  const size = product.sizes?.find((s) => s.id === state.sizeId);
  if (size?.label) props["Size"] = size.label;
  if (product.materials && (state.materialId || state.colorwayId)) {
    const mat = product.materials.find((m) => m.id === state.materialId);
    const cw = mat?.colorways.find((c) => c.id === state.colorwayId);
    if (mat?.label) props["Fabric"] = mat.label;
    if (cw?.label) props["Colour"] = cw.label;
  } else {
    const fabric = product.fabrics.find((f) => f.id === state.fabricId);
    if (fabric?.label) props["Fabric"] = fabric.label;
  }
  const leg = product.legs.find((g) => g.id === state.legId);
  if (leg?.label) props["Legs"] = leg.label;
  return props;
}

function numericId(variantId: string): string {
  const m = /ProductVariant\/(\d+)/.exec(variantId);
  return m ? m[1] : variantId;
}

function inIframe(): boolean {
  try {
    return window.top !== window.self;
  } catch {
    return true; // cross-origin access threw → we are embedded
  }
}

export type BuyOutcome =
  | { status: "navigating" }
  | { status: "posted" }
  | { status: "blocked"; message: string };

/**
 * Add the configured pieces to the merchant's NORMAL Shopify cart.
 *  - embedded (the usual case): postMessage the real line items to the host page;
 *    the Neovaa embed snippet calls /cart/add.js with the configuration properties
 *    and reveals the cart, so the shopper stays on the product page.
 *  - standalone (a bare viewer URL, and the testing path): navigate to a cart
 *    permalink, which drops the real variants into the normal cart natively.
 */
// Broadcast the current configuration to the host product page so the merchant's
// OWN price element and Add-to-cart button reflect the configuration (instead of the
// configurator showing a separate price in a separate block). The embed snippet
// listens for this and updates the native price + wires the native button to add the
// configured pieces. No-op unless embedded in a Shopify-sourced product page.
export function broadcastState(product: ProductConfig, state: ConfigState, live: LivePrice | null, buyable = true): void {
  if (!shopifyActive(product) || !inIframe()) return;
  const bom = resolveBOM(product, state);
  const properties = configProperties(product, state);
  const items = bom.ok
    ? bom.lines.map((l) => ({ id: numericId(l.variantId), quantity: l.quantity, properties }))
    : [];
  window.parent.postMessage(
    {
      type: "neovaa:state",
      price: live ? live.total : null,
      currency: live ? live.currency : product.currency,
      available: live ? live.allAvailable : true,
      // `buyable=false` (variant not found in the store) must stop the host wiring its
      // native Add-to-cart to a non-existent variant, even though the BOM maps locally.
      canBuy: buyable && bom.ok && items.length > 0,
      items,
    },
    "*",
  );
}

// In embed mode, keep the host iframe sized to the configurator's real content so
// there is no inner scrollbar and nothing is cut off. Posts the content height on
// load, on resize, and whenever the body box changes; plus a few delayed posts to
// catch async layout settling (fonts, the 3D reveal).
export function startEmbedAutoHeight(): void {
  if (!inIframe()) return;
  const post = () => {
    const h = Math.ceil(document.documentElement.scrollHeight);
    try {
      window.parent.postMessage({ type: "neovaa:height", height: h }, "*");
    } catch {
      /* ignore */
    }
  };
  post();
  window.addEventListener("resize", post);
  if (typeof ResizeObserver !== "undefined") {
    try {
      new ResizeObserver(post).observe(document.body);
    } catch {
      /* ignore */
    }
  }
  [200, 600, 1500, 3000].forEach((ms) => setTimeout(post, ms));
}

export function buyShopify(product: ProductConfig, state: ConfigState): BuyOutcome {
  const bom = resolveBOM(product, state);
  if (!bom.ok) {
    if (bom.reason === "unmapped") {
      return { status: "blocked", message: "This combination isn't available to buy yet" };
    }
    return { status: "blocked", message: "Buying isn't set up for this product yet" };
  }
  const shopDomain = product.shopify?.shopDomain;
  const properties = configProperties(product, state);
  const items = bom.lines.map((l) => ({ id: numericId(l.variantId), quantity: l.quantity, properties }));

  if (inIframe()) {
    try {
      window.parent.postMessage({ type: "neovaa:add-to-cart", items, shopDomain }, "*");
      return { status: "posted" };
    } catch {
      /* fall through to the permalink */
    }
  }
  if (shopDomain) {
    const path = bom.lines.map((l) => `${numericId(l.variantId)}:${l.quantity}`).join(",");
    goToCheckout(`https://${shopDomain}/cart/${path}`);
    return { status: "navigating" };
  }
  return { status: "blocked", message: "Store not configured for checkout" };
}
